import {
  Button,
  Calendar,
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@egaranti/components";

import { endOfMonth, format, startOfMonth, subDays, subMonths } from "date-fns";
import { tr, uz } from "date-fns/locale";
import { Calendar as CalendarIcon } from "lucide-react";
import { useTranslation } from "react-i18next";

const DateRangePicker = ({ dateRange, onChange }) => {
  const { t, i18n } = useTranslation();
  const dateLocale = i18n.language === "uz" ? uz : tr;
  const presets = [
    {
      label: t("extendedWarranty.reports.datePicker.today", "Bugün"),
      range: () => ({
        from: new Date(),
        to: new Date(),
      }),
    },
    {
      label: t("extendedWarranty.reports.datePicker.last7Days", "Son 7 Gün"),
      range: () => ({
        from: subDays(new Date(), 6),
        to: new Date(),
      }),
    },
    {
      label: t("extendedWarranty.reports.datePicker.thisMonth", "Bu Ay"),
      range: () => ({
        from: startOfMonth(new Date()),
        to: new Date(),
      }),
    },
    {
      label: t("extendedWarranty.reports.datePicker.last3Months", "Son 3 Ay"),
      range: () => ({
        from: subMonths(new Date(), 3),
        to: new Date(),
      }),
    },
    {
      label: t("extendedWarranty.reports.datePicker.last6Months", "Son 6 Ay"),
      range: () => ({
        from: subMonths(new Date(), 6),
        to: new Date(),
      }),
    },
    {
      label: t("extendedWarranty.reports.datePicker.thisYear", "Bu Yıl"),
      range: () => ({
        from: new Date(new Date().getFullYear(), 0, 1),
        to: new Date(),
      }),
    },
  ];

  const handlePresetClick = (preset) => {
    onChange(preset.range());
  };

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          variant="secondaryGray"
          className="h-12 w-full justify-start text-left shadow-none transition-colors md:w-auto"
        >
          <CalendarIcon className="mr-2 h-4 w-4 text-gray-500" />
          <span className="text-gray-900">
            {dateRange?.from && dateRange?.to
              ? `${format(new Date(dateRange.from), "d MMM yyyy", { locale: dateLocale })} - ${format(new Date(dateRange.to), "d MMM yyyy", { locale: dateLocale })}`
              : t("extendedWarranty.reports.datePicker.placeholder", "Tarih Aralığı Seçiniz")}
          </span>
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="w-auto max-w-md border border-gray-200 bg-white p-0 shadow-lg"
        align="start"
      >
        <div className="flex flex-col">
          {/* Preset Buttons */}
          <div className="border-b border-gray-100 p-3">
            <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
              {t("extendedWarranty.reports.datePicker.quickSelect", "Hızlı Seçim")}
            </div>
            <div className="grid grid-cols-3 gap-1">
              {presets.map((preset) => (
                <Button
                  key={preset.label}
                  variant="secondaryGray"
                  size="sm"
                  onClick={() => handlePresetClick(preset)}
                  className="px-2 py-1 text-xs font-normal transition-colors hover:bg-blue-50 hover:text-blue-700"
                >
                  {preset.label}
                </Button>
              ))}
            </div>
          </div>

          {/* Calendar */}
          <div className="p-4">
            <Calendar
              initialFocus
              captionLayout="dropdown-years"
              mode="range"
              defaultMonth={dateRange?.from || new Date()}
              selected={dateRange}
              onSelect={onChange}
              numberOfMonths={1}
              locale={dateLocale}
            />
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default DateRangePicker;
